import { components } from '@apiSchema';

type MacDetailResponse = components['schemas']['MacDetailResponse'];
type MacStageResponse = components['schemas']['MacStageResponse'];
type MacKeywordResponse = components['schemas']['MacKeywordResponse'];

export type MacStageKey = 'inception' | 'adoption' | 'proliferation' | 'ubiquity';

export const MAC_STAGE_KEYS: MacStageKey[] = ['inception', 'adoption', 'proliferation', 'ubiquity'];

export const MAC_FONT_SIZE_MIN = 8;
export const MAC_FONT_SIZE_MAX = 20;
export const MAC_FONT_SIZE_DEFAULT = 10;

const US_COUNTRY_CODE = 'USA';
// Countries whose MACs offer the keyword menu (food profile + menu examples).
const KEYWORD_MENU_COUNTRY_CODES = [US_COUNTRY_CODE, 'CAN'];

export interface IMacKeyword {
    id: number;
    text: string;
    // SNAP keyword: only these have a food profile to link to (US MACs only).
    isKeyword: boolean;
}

export interface IMacStage {
    key: MacStageKey;
    keywords: IMacKeyword[];
}

export interface IMacDetails {
    id: number;
    title: string;
    countryCode: string;
    isNonUs: boolean;
    hasKeywordMenu: boolean;
    fontSize: number;
    stages: IMacStage[];
}

// BE sends the stage as a free string: accept the stage name or its 1-based position.
function getStageKey(stage: string | null | undefined): MacStageKey | null {
    const normalized = (stage ?? '').trim().toLowerCase();
    const byName = MAC_STAGE_KEYS.find((key) => key === normalized);
    if (byName) {
        return byName;
    }

    return MAC_STAGE_KEYS[Number(normalized) - 1] ?? null;
}

function isValidKeyword(keyword: MacKeywordResponse): keyword is MacKeywordResponse & { id: number; text: string } {
    return typeof keyword.id === 'number' && !!keyword.text;
}

export function clampMacFontSize(fontSize: number | null | undefined): number {
    if (typeof fontSize !== 'number' || !Number.isFinite(fontSize)) {
        return MAC_FONT_SIZE_DEFAULT;
    }

    return Math.min(MAC_FONT_SIZE_MAX, Math.max(MAC_FONT_SIZE_MIN, Math.round(fontSize)));
}

function getStageKeywords(stages: MacStageResponse[], key: MacStageKey): IMacKeyword[] {
    return stages
        .filter((stage) => getStageKey(stage.stage) === key)
        .flatMap((stage) => (stage.keywords ?? []).filter(isValidKeyword))
        .map(({ id, text, isKeyword }) => ({ id, text, isKeyword: isKeyword ?? false }));
}

export function mapMacDetails(dto: MacDetailResponse, fallbackId: number): IMacDetails {
    const stages = dto.stages ?? [];
    const countryCode = dto.countryCode ?? US_COUNTRY_CODE;

    return {
        id: dto.id ?? fallbackId,
        title: dto.title ?? '',
        countryCode,
        isNonUs: countryCode.toUpperCase() !== US_COUNTRY_CODE,
        hasKeywordMenu: KEYWORD_MENU_COUNTRY_CODES.includes(countryCode.toUpperCase()),
        fontSize: clampMacFontSize(dto.fontSize),
        stages: MAC_STAGE_KEYS.map((key) => ({ key, keywords: getStageKeywords(stages, key) })),
    };
}
